import { ausserhalb, blaetterSlots, slotAnzahl } from "./form.ts";
import type { Operation, Slot, UiDaten } from "./modell.ts";

export class OperationsFehler extends Error {}

type Ergebnis = { ui: UiDaten; umkehr: Operation[] };
type Felder = Record<string, unknown>;

function pruefeIndex(ui: UiDaten, i: number): void {
  if (i < 0 || i >= slotAnzahl(ui.form)) throw new OperationsFehler("Slot liegt außerhalb der Form.");
}

function pruefeFrei(ui: UiDaten, i: number): void {
  if (ui.listen.some((l) => l.slots.includes(i)) || blaetterSlots(ui).includes(i)) {
    throw new OperationsFehler("Slot gehört zu einem Listenbereich.");
  }
}

/** Operation, die Slot i vom Stand `jetzt` zurück auf `alt` bringt. */
function wiederherstellen(i: number, alt: Slot | undefined, jetzt: Slot | undefined): Operation {
  if (!alt) return { art: "slots_leeren", indizes: [i] };
  const felder: Felder = { ...alt };
  for (const k of Object.keys(jetzt ?? {})) if (!(k in alt)) felder[k] = null;
  return { art: "slot_setzen", index: i, felder: felder as Extract<Operation, { art: "slot_setzen" }>["felder"] };
}

function verkleinere(ui: UiDaten): void {
  const n = slotAnzahl(ui.form);
  for (const k of Object.keys(ui.slots)) if (Number(k) >= n) delete ui.slots[k];
  for (const l of ui.listen) {
    l.slots = l.slots.filter((i) => i < n);
    const b = l.blaettern;
    if (!b) continue;
    if (b.zurueck && b.zurueck.slot >= n) delete b.zurueck;
    if (b.weiter && b.weiter.slot >= n) delete b.weiter;
    if (b.info && b.info.slot >= n) delete b.info;
    if (!b.zurueck && !b.weiter && !b.info) delete l.blaettern;
  }
  ui.listen = ui.listen.filter((l) => l.slots.length > 0);
}

export function wendeAn(ui: UiDaten, op: Operation): Ergebnis {
  const neu = structuredClone(ui);
  switch (op.art) {
    case "slot_setzen": {
      pruefeIndex(ui, op.index);
      pruefeFrei(ui, op.index);
      const alt = ui.slots[op.index];
      const slot: Felder = { ...structuredClone(alt ?? {}) };
      for (const [k, v] of Object.entries(op.felder)) {
        if (v === undefined) continue;
        if (v === null) delete slot[k];
        else slot[k] = structuredClone(v);
      }
      if (!slot.symbol) throw new OperationsFehler("Item fehlt.");
      neu.slots[op.index] = slot as Slot;
      if (!alt) return { ui: neu, umkehr: [{ art: "slots_leeren", indizes: [op.index] }] };
      const zurueck: Felder = {};
      for (const [k, v] of Object.entries(op.felder)) if (v !== undefined) zurueck[k] = k in alt ? structuredClone((alt as Felder)[k]) : null;
      return { ui: neu, umkehr: [{ art: "slot_setzen", index: op.index, felder: zurueck as Extract<Operation, { art: "slot_setzen" }>["felder"] }] };
    }
    case "slots_leeren": {
      const umkehr: Operation[] = [];
      for (const i of op.indizes) {
        const alt = ui.slots[i];
        if (!alt) continue;
        delete neu.slots[i];
        umkehr.push(wiederherstellen(i, alt, undefined));
      }
      return { ui: neu, umkehr };
    }
    case "slots_verschieben": {
      const quellen = new Set(op.paare.map((p) => p.von));
      const ziele = new Set(op.paare.map((p) => p.nach));
      if (ziele.size !== op.paare.length) throw new OperationsFehler("Ein Ziel kommt doppelt vor.");
      for (const p of op.paare) {
        pruefeIndex(ui, p.von);
        pruefeIndex(ui, p.nach);
        pruefeFrei(ui, p.nach);
      }
      if (op.kopieren) {
        for (const p of op.paare) {
          const s = ui.slots[p.von];
          if (s) neu.slots[p.nach] = structuredClone(s);
        }
      } else {
        const freie = op.paare.map((p) => p.von).filter((v) => !ziele.has(v));
        const verdraengt = op.paare.map((p) => p.nach).filter((n) => ui.slots[n] && !quellen.has(n));
        if (verdraengt.length > freie.length) throw new OperationsFehler("Für belegte Ziele ist kein Platz frei.");
        for (const v of quellen) delete neu.slots[v];
        for (const p of op.paare) {
          const s = ui.slots[p.von];
          if (s) neu.slots[p.nach] = structuredClone(s);
          else delete neu.slots[p.nach];
        }
        verdraengt.forEach((n, i) => {
          neu.slots[freie[i]!] = structuredClone(ui.slots[n]!);
        });
      }
      const betroffen = [...new Set([...quellen, ...ziele])];
      return { ui: neu, umkehr: betroffen.map((i) => wiederherstellen(i, ui.slots[i], neu.slots[i])) };
    }
    case "ui_setzen": {
      const f = op.felder;
      const zurueck: Felder = {};
      if (f.titel !== undefined) {
        zurueck.titel = ui.titel;
        neu.titel = f.titel;
      }
      if (f.rahmen !== undefined) {
        zurueck.rahmen = ui.rahmen;
        neu.rahmen = structuredClone(f.rahmen);
      }
      if (f.text !== undefined) {
        zurueck.text = ui.text;
        neu.text = [...f.text];
      }
      if (f.quelle !== undefined) {
        zurueck.quelle = ui.quelle ?? null;
        if (f.quelle === null) delete neu.quelle;
        else neu.quelle = f.quelle;
      }
      if (f.form !== undefined) {
        zurueck.form = ui.form;
        const verloren = ausserhalb(ui, f.form).length > 0;
        neu.form = structuredClone(f.form);
        if (verloren) {
          verkleinere(neu);
          const listen: Operation[] = ui.listen.flatMap((l, stelle): Operation[] => {
            const jetzt = neu.listen.find((x) => x.name === l.name);
            if (jetzt && JSON.stringify(jetzt) === JSON.stringify(l)) return [];
            return [{ art: "listenbereich_setzen", bereich: structuredClone(l), position: stelle }];
          });
          const slots = Object.keys(ui.slots)
            .filter((k) => !neu.slots[k])
            .map((k) => wiederherstellen(Number(k), ui.slots[k], undefined));
          return { ui: neu, umkehr: [{ art: "ui_setzen", felder: zurueck as Extract<Operation, { art: "ui_setzen" }>["felder"] }, ...listen, ...slots] };
        }
      }
      return { ui: neu, umkehr: [{ art: "ui_setzen", felder: zurueck as Extract<Operation, { art: "ui_setzen" }>["felder"] }] };
    }
    case "listenbereich_setzen": {
      const b = op.bereich;
      const n = slotAnzahl(ui.form);
      if (b.slots.some((i) => i >= n)) throw new OperationsFehler("Slot liegt außerhalb der Form.");
      if (new Set(b.slots).size !== b.slots.length) throw new OperationsFehler("Ein Slot kommt im Listenbereich doppelt vor.");
      const andere = ui.listen.filter((l) => l.name !== b.name);
      if (andere.some((l) => l.slots.some((i) => b.slots.includes(i)))) throw new OperationsFehler("Listenbereiche überlappen sich.");
      const stelle = ui.listen.findIndex((l) => l.name === b.name);
      const altBereich = stelle >= 0 ? ui.listen[stelle] : undefined;
      const verdraengt = b.slots.filter((i) => ui.slots[i]);
      for (const i of verdraengt) delete neu.slots[i];
      if (stelle >= 0) neu.listen[stelle] = structuredClone(b);
      else neu.listen.splice(Math.min(op.position ?? neu.listen.length, neu.listen.length), 0, structuredClone(b));
      const umkehr: Operation[] = [
        altBereich ? { art: "listenbereich_setzen", bereich: altBereich } : { art: "listenbereich_entfernen", name: b.name },
        ...verdraengt.map((i) => wiederherstellen(i, ui.slots[i], undefined)),
      ];
      return { ui: neu, umkehr };
    }
    case "listenbereich_entfernen": {
      const stelle = ui.listen.findIndex((l) => l.name === op.name);
      if (stelle < 0) throw new OperationsFehler("Diesen Listenbereich gibt es nicht.");
      neu.listen.splice(stelle, 1);
      return { ui: neu, umkehr: [{ art: "listenbereich_setzen", bereich: ui.listen[stelle]!, position: stelle }] };
    }
    case "alles_ersetzen":
      return { ui: structuredClone(op.daten), umkehr: [{ art: "alles_ersetzen", daten: ui }] };
  }
}

export function wendeAlleAn(ui: UiDaten, ops: Operation[]): Ergebnis {
  let aktuell = ui;
  const umkehr: Operation[][] = [];
  for (const op of ops) {
    const r = wendeAn(aktuell, op);
    aktuell = r.ui;
    umkehr.unshift(r.umkehr);
  }
  return { ui: aktuell, umkehr: umkehr.flat() };
}
